/**
 * Fase 3 — Prøvesett steg 1: Grunninfo.
 * Studieplan, modul-dekning, tittel, beskrivelse, gyldighet.
 */
import { useEffect, useState } from 'react';
import { Sparkles, Loader2, FileText, Info } from 'lucide-react';
import { authenticatedFetch, getUser } from '../../../lib/auth';
import type { StudyPlan } from '../../../types';
import type { ExamSetWizardData } from '../NewExamSetWizard';
import InlineHelpTerm from '../InlineHelpTerm';

interface PlanModule {
  id: string;
  module_code: string;
  title: string;
  ects_credits: number | null;
  estimated_hours: number | null;
}

export interface BasicsStepProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
  aiScaffoldMode: boolean;
  scaffoldLoading: boolean;
  onTriggerScaffold: () => void;
}

export default function BasicsStep({
  data,
  patch,
  aiScaffoldMode,
  scaffoldLoading,
  onTriggerScaffold,
}: BasicsStepProps) {
  const [studyPlans, setStudyPlans] = useState<StudyPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planModules, setPlanModules] = useState<PlanModule[]>([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Last godkjente studieplaner
  useEffect(() => {
    (async () => {
      setLoadingPlans(true);
      setLoadError(null);
      try {
        const user = await getUser();
        if (!user) throw new Error('Ikke innlogget');
        const res = await authenticatedFetch('/api/study-plans/list', {
          method: 'POST',
          body: JSON.stringify({ user_id: user.id }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Kunne ikke laste studieplaner');
        setStudyPlans((json.study_plans || []).filter((sp: StudyPlan) => sp.status === 'approved'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Ukjent feil';
        setLoadError(msg);
      } finally {
        setLoadingPlans(false);
      }
    })();
  }, []);

  // Last moduler for valgt plan
  useEffect(() => {
    if (!data.study_plan_id) {
      setPlanModules([]);
      return;
    }
    (async () => {
      setLoadingModules(true);
      try {
        const res = await authenticatedFetch(`/api/ai/modules/${data.study_plan_id}`);
        const json = await res.json();
        setPlanModules(json.success && Array.isArray(json.modules) ? json.modules : []);
      } catch {
        setPlanModules([]);
      } finally {
        setLoadingModules(false);
      }
    })();
  }, [data.study_plan_id]);

  const aggregatedEcts = planModules
    .filter((m) => data.included_module_ids.includes(m.id))
    .reduce((sum, m) => sum + (m.ects_credits || 0), 0);

  return (
    <div className="space-y-5">
      {/* KI-scaffold-trigger (vises kun i KI-modus når studieplan er valgt) */}
      {aiScaffoldMode && data.study_plan_id && (
        <div className="rounded-lg bg-gradient-to-br from-kf-orange-50 to-white border border-kf-orange-200 p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-kf-orange-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-kf-gray-900 mb-1">
                <InlineHelpTerm
                  slug="wizard-ki-scaffold"
                  label="KI-forslag"
                  route="/courses-and-exams"
                />{' '}
                basert på studieplan
              </h4>
              <p className="text-sm text-kf-gray-700 mb-3">
                KI leser studieplanen og foreslår tittel, beskrivelse, varighet, beståttgrense, karakterskala, gyldighet
                og spørsmål. Du kan justere alt i de neste stegene.
              </p>
              <button
                type="button"
                onClick={onTriggerScaffold}
                disabled={scaffoldLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-kf-orange-500 hover:bg-kf-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {scaffoldLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {scaffoldLoading ? 'Henter forslag…' : data.title ? 'Hent nytt forslag' : 'Hent KI-forslag'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Studieplan */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          Studieplan <span className="text-red-500">*</span>
        </label>
        {loadingPlans ? (
          <div className="flex items-center gap-2 text-sm text-kf-gray-600 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Laster godkjente studieplaner…
          </div>
        ) : loadError ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {loadError}
          </div>
        ) : (
          <select
            value={data.study_plan_id}
            onChange={(e) =>
              patch({
                study_plan_id: e.target.value,
                included_module_ids: [],
                module_coverage: 'full',
              })
            }
            className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          >
            <option value="">— Velg studieplan —</option>
            {studyPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.title} ({plan.ects_credits ?? 0} KP)
              </option>
            ))}
          </select>
        )}
        {!loadingPlans && !loadError && studyPlans.length === 0 && (
          <p className="text-xs text-kf-gray-500 mt-1">
            Ingen godkjente studieplaner tilgjengelig. Opprett og godkjenn en studieplan først.
          </p>
        )}
      </div>

      {/* Modul-dekning */}
      {data.study_plan_id && planModules.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-2">
            Hva dekker prøvesettet?
          </label>
          <div className="flex flex-wrap gap-4 mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="module-coverage"
                checked={data.module_coverage === 'full'}
                onChange={() => patch({ module_coverage: 'full', included_module_ids: [] })}
              />
              <span>Hele studieplanen ({planModules.length} moduler)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="module-coverage"
                checked={data.module_coverage === 'subset'}
                onChange={() => patch({ module_coverage: 'subset' })}
              />
              <span>Et utvalg moduler</span>
            </label>
          </div>

          {data.module_coverage === 'subset' && (
            <div className="bg-kf-gray-50 border border-kf-gray-200 rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs text-kf-gray-600 mb-2">
                Velg modulene prøvesettet skal dekke. Kompetanseattesten reflekterer kun disse modulene.
              </p>
              {planModules.map((mod) => {
                const checked = data.included_module_ids.includes(mod.id);
                return (
                  <label
                    key={mod.id}
                    className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        patch({
                          included_module_ids: e.target.checked
                            ? [...data.included_module_ids, mod.id]
                            : data.included_module_ids.filter((x) => x !== mod.id),
                        });
                      }}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-kf-gray-900">
                        {mod.module_code}: {mod.title}
                      </div>
                      <div className="text-xs text-kf-gray-500">
                        {mod.ects_credits ? `${mod.ects_credits} KP` : ''}
                        {mod.ects_credits && mod.estimated_hours ? ' · ' : ''}
                        {mod.estimated_hours ? `${mod.estimated_hours}t` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
              {data.included_module_ids.length > 0 && (
                <div className="text-xs text-kf-blue-700 font-medium pt-2 border-t border-kf-gray-200">
                  Aggregert for prøvesettet: {aggregatedEcts} KP
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loadingModules && (
        <div className="flex items-center gap-2 text-xs text-kf-gray-500">
          <Loader2 className="w-3 h-3 animate-spin" />
          Laster moduler…
        </div>
      )}

      {/* Tittel */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          Tittel på prøvesettet <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="F.eks: Avsluttende prøve for ledelseskurs"
          className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
      </div>

      {/* Beskrivelse */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">Beskrivelse</label>
        <textarea
          value={data.description}
          onChange={(e) => patch({ description: e.target.value })}
          rows={3}
          placeholder="Kort beskrivelse av prøven (vises for kandidater)…"
          className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
      </div>

      {/* Gyldighet */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          <InlineHelpTerm
            slug="wizard-certificate-validity"
            label="Gyldighet på attest"
            route="/courses-and-exams"
          />
          {' '}(år)
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={data.validity_years ?? ''}
            onChange={(e) => patch({ validity_years: e.target.value ? parseInt(e.target.value) : null })}
            placeholder="Evig gyldig"
            min={1}
            max={99}
            className="w-32 px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          />
          <span className="text-sm text-kf-gray-600">
            {data.validity_years ? `Attesten utløper etter ${data.validity_years} år` : 'Attesten har ingen utløpsdato'}
          </span>
        </div>
      </div>

      <div className="rounded-md bg-kf-gray-50 border border-kf-gray-200 p-3 text-xs text-kf-gray-600 flex items-start gap-2">
        <Info className="w-4 h-4 text-kf-gray-500 shrink-0 mt-0.5" />
        <div>
          <p className="mb-1 font-medium text-kf-gray-700 flex items-center gap-1">
            <FileText className="w-3 h-3" /> Tips
          </p>
          Når du har valgt studieplan + modul-dekning + tittel går du videre til <strong>Vurdering</strong>.
          Resten av stegene gir deg kontroll over tid, karakterskala, matrise, SEB og spørsmål.
        </div>
      </div>
    </div>
  );
}
